// src/components/offer/OfferWritersEditor.tsx
// VIB-789: Redigerer tilbudsskrivere med share_pct. Sum = 100 håndheves.
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { useAssignableUsers } from "@/hooks/use-users";

export interface Writer {
  user_id: string;
  share_pct: number;
}

interface Props {
  writers: Writer[];
  onChange: (next: Writer[]) => void;
  /** Tilbudsskriving-linjas total i timer; vises ved siden av hver writer. */
  totalHours?: number;
  disabled?: boolean;
}

export function OfferWritersEditor({ writers, onChange, totalHours, disabled }: Props) {
  const { data: users = [] } = useAssignableUsers();
  const userById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.id, u.full_name);
    return m;
  }, [users]);

  const sumPct = writers.reduce((s, w) => s + (w.share_pct ?? 0), 0);
  const sumOk = Math.abs(sumPct - 100) < 0.01;

  function update(idx: number, patch: Partial<Writer>) {
    const next = writers.map((w, i) => (i === idx ? { ...w, ...patch } : w));
    onChange(next);
  }

  function remove(idx: number) {
    onChange(writers.filter((_, i) => i !== idx));
  }

  function add() {
    const usedIds = new Set(writers.map((w) => w.user_id));
    const firstFree = users.find((u) => !usedIds.has(u.id));
    if (!firstFree) return;
    onChange([...writers, { user_id: firstFree.id, share_pct: 0 }]);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium">Tilbudsskrivere</label>
        <span
          className={
            sumOk
              ? "text-[11px] text-muted-foreground"
              : "text-[11px] text-red-600 font-medium"
          }
        >
          Sum: {sumPct.toFixed(0)}%
        </span>
      </div>

      <div className="space-y-1.5">
        {writers.map((w, idx) => {
          const writerHours =
            totalHours != null ? (totalHours * (w.share_pct ?? 0)) / 100 : null;
          return (
            <div key={idx} className="flex items-center gap-2">
              <Select
                value={w.user_id}
                onValueChange={(v) => update(idx, { user_id: v })}
                disabled={disabled}
              >
                <SelectTrigger className="h-8 flex-1 text-sm">
                  <SelectValue>
                    {userById.get(w.user_id) ?? "Velg…"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                min={0}
                max={100}
                step={1}
                value={w.share_pct}
                onChange={(e) =>
                  update(idx, { share_pct: Number(e.target.value) })
                }
                disabled={disabled}
                className="h-8 w-20 text-sm"
              />
              <span className="text-[11px] text-muted-foreground w-12">
                {writerHours != null
                  ? `${writerHours.toFixed(1).replace(".", ",")}t`
                  : "%"}
              </span>
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={disabled || writers.length <= 1}
                className="text-muted-foreground hover:text-red-600 disabled:opacity-30"
                aria-label="Fjern writer"
              >
                <SrIcon name="x" className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        disabled={disabled || users.length <= writers.length}
        className="h-7 text-xs"
      >
        + Legg til writer
      </Button>
    </div>
  );
}
